/**
 * Notes per reconciliation line (#1123): a discount agreed with the customer, the next step. Notes are keyed by
 * `line_key` (`sub:<id>` or `ubb:<company>:<family>`), so they survive every rebuild. The thread follows
 * `billing/bill-notes.tsx` (author · time, body, one box to add); here it lives in a drawer and your own notes can be
 * edited or deleted (the delete is soft on the server).
 */
import { Button, Drawer, ErrorBanner, SkeletonRows, Textarea, toast } from '@patchbay/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { MessageSquareText, Plus } from 'lucide-react'
import * as React from 'react'
import { describeError, unwrap } from '../../lib/api/client'
import {
  createLicenceNoteApiV1LicencesNotesPost,
  deleteLicenceNoteApiV1LicencesNotesNoteIdDelete,
  type LicenceNoteOut,
  updateLicenceNoteApiV1LicencesNotesNoteIdPatch,
} from '../../lib/api/generated'
import { formatWhen } from '../../lib/display-time'
import { useMe } from '../auth/session'
import { canSeeLicences, licenceKeys, notesQuery } from './common'

function stamp(iso: string): string {
  const date = new Date(iso)
  return Number.isNaN(date.valueOf()) ? iso : formatWhen(date.toISOString(), { style: 'absolute' })
}

const authorOf = (note: LicenceNoteOut): string => note.author?.name ?? note.author_name ?? 'Someone'

function useNoteMutations(lineKey: string) {
  const queryClient = useQueryClient()
  // The line's notes_count lives on every licences read, so refresh them all.
  const refresh = () => queryClient.invalidateQueries({ queryKey: licenceKeys.all })
  const onError = (what: string) => (error: unknown) => toast.error(`${what}: ${describeError(error)}`)
  const add = useMutation({
    mutationFn: (body: string) => unwrap(createLicenceNoteApiV1LicencesNotesPost({ body: { line_key: lineKey, body } })),
    onSuccess: refresh,
    onError: onError('Note not added'),
  })
  const edit = useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) =>
      unwrap(updateLicenceNoteApiV1LicencesNotesNoteIdPatch({ path: { note_id: id }, body: { body } })),
    onSuccess: refresh,
    onError: onError('Note not saved'),
  })
  const remove = useMutation({
    mutationFn: (id: string) => unwrap(deleteLicenceNoteApiV1LicencesNotesNoteIdDelete({ path: { note_id: id } })),
    onSuccess: refresh,
    onError: onError('Note not deleted'),
  })
  return { add, edit, remove }
}

function NoteItem({ note, mine, lineKey }: { note: LicenceNoteOut; mine: boolean; lineKey: string }) {
  const { edit, remove } = useNoteMutations(lineKey)
  const [draft, setDraft] = React.useState<string | null>(null)
  const edited = note.updated_at !== note.created_at
  return (
    <li className="space-y-1 rounded-md border border-border bg-card px-3 py-2 text-sm" data-testid="licence-note">
      <p className="flex flex-wrap items-baseline gap-x-1 text-xs text-muted-foreground">
        <span className="font-medium text-foreground">{authorOf(note)}</span>
        <span className="text-subtle">·</span>
        <span>{stamp(note.created_at)}</span>
        {edited ? <span className="text-subtle">(edited)</span> : null}
        {note.imported_from ? <span className="text-subtle">· imported</span> : null}
      </p>
      {draft === null ? (
        <p className="whitespace-pre-wrap break-words">{note.body}</p>
      ) : (
        <form
          className="space-y-1.5"
          onSubmit={(e) => {
            e.preventDefault()
            const body = draft.trim()
            if (body) edit.mutate({ id: note.id, body }, { onSuccess: () => setDraft(null) })
          }}
        >
          <Textarea aria-label="Edit note" rows={3} value={draft} onChange={(e) => setDraft(e.currentTarget.value)} autoFocus />
          <div className="flex gap-1.5">
            <Button type="submit" size="sm" variant="primary" disabled={edit.isPending || !draft.trim()}>
              Save
            </Button>
            <Button type="button" size="sm" onClick={() => setDraft(null)}>
              Cancel
            </Button>
          </div>
        </form>
      )}
      {mine && draft === null ? (
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" onClick={() => setDraft(note.body)}>
            Edit
          </Button>
          <Button size="sm" variant="ghost" disabled={remove.isPending} onClick={() => remove.mutate(note.id)}>
            Delete
          </Button>
        </div>
      ) : null}
    </li>
  )
}

export function NotesThread({ lineKey }: { lineKey: string }) {
  const me = useMe()
  const q = useQuery(notesQuery(lineKey))
  const { add } = useNoteMutations(lineKey)
  const [body, setBody] = React.useState('')
  const canWrite = canSeeLicences(me?.role)
  const submit = () => {
    const text = body.trim()
    if (text) add.mutate(text, { onSuccess: () => setBody('') })
  }
  return (
    <div className="space-y-3" data-testid="licence-notes">
      {q.isPending ? (
        <SkeletonRows count={2} />
      ) : q.isError ? (
        <ErrorBanner title="Notes could not load" detail={describeError(q.error)} onRetry={() => void q.refetch()} />
      ) : q.data.length === 0 ? (
        <p className="text-sm text-muted-foreground">No notes on this line yet.</p>
      ) : (
        <ul aria-label="Notes" className="space-y-2">
          {q.data.map((note) => (
            <NoteItem key={note.id} note={note} lineKey={lineKey} mine={Boolean(me && note.author?.id === me.id)} />
          ))}
        </ul>
      )}
      {canWrite ? (
        <form
          className="flex flex-col gap-1.5"
          onSubmit={(e) => {
            e.preventDefault()
            submit()
          }}
        >
          <Textarea
            aria-label="Add a note about this line"
            placeholder="A discount agreed, the next step…"
            rows={3}
            value={body}
            onChange={(e) => setBody(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault()
                submit()
              }
            }}
          />
          <div className="flex items-center gap-2">
            <Button type="submit" size="sm" variant="primary" disabled={add.isPending || !body.trim()}>
              Add note
            </Button>
            <span className="text-xs text-subtle">Ctrl+Enter</span>
          </div>
        </form>
      ) : null}
    </div>
  )
}

/** The Notes cell: the count, or "Add note"; opens the line's thread in a drawer. */
export function NotesButton({ lineKey, count, title, subtitle }: { lineKey: string; count: number; title: string; subtitle?: string }) {
  const [open, setOpen] = React.useState(false)
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={count > 0 ? `${count} note${count === 1 ? '' : 's'} on ${title}` : `Add a note on ${title}`}
        className={
          count > 0
            ? 'inline-flex h-6 cursor-pointer items-center gap-1 rounded-md bg-muted px-2 text-xs font-medium text-foreground hover:bg-accent [&_svg]:size-3.5'
            : 'inline-flex h-6 cursor-pointer items-center gap-1 rounded-md border border-dashed border-border px-2 text-xs text-muted-foreground hover:border-primary hover:text-foreground [&_svg]:size-3.5'
        }
      >
        {count > 0 ? <MessageSquareText aria-hidden /> : <Plus aria-hidden />}
        {count > 0 ? count : 'Note'}
      </button>
      {open ? (
        <Drawer open={open} onOpenChange={setOpen} title={`Notes · ${title}`} description={subtitle}>
          <NotesThread lineKey={lineKey} />
        </Drawer>
      ) : null}
    </>
  )
}
